import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import i18n from '../i18n';
import stompClient from '../services/stompClient';
import { getToken, surChangementJeton } from '../variables/constants';
import {
  compteurDansTitre, demanderPermissionNotification, notifierNavigateur, sonDiscret,
} from '../pages/conversations/utils';

/**
 * Alerte de messagerie interne, active sur TOUTES les pages.
 *
 * Monté dans la NavBar (donc dans chaque Layout), ce composant sans rendu ouvre
 * la connexion STOMP dès la connexion et écoute la file privée de l'agent. À
 * chaque message reçu : toast cliquable qui mène à la conversation, son discret,
 * compteur dans le titre de l'onglet et notification navigateur si l'onglet n'a
 * pas le focus, et événement `cb:conversations-non-lus` qui met à jour les
 * pastilles rouges (NavBar, sidebar).
 *
 * La page Conversations, elle, ne fait que tenir sa liste à jour : l'alerte
 * n'est jamais doublée. On se tait seulement si la conversation concernée est
 * ouverte ET que l'onglet a le focus - l'agent la voit déjà.
 */
export default function AlertesConversations() {
  const navigate = useNavigate();
  const location = useLocation();
  const cheminRef = useRef(location.pathname);
  cheminRef.current = location.pathname;

  // Meme garde que dans AppelProvider, meme raison de prendre le jeton en
  // dependance : ce composant vit dans la NavBar, donc apres la connexion, mais
  // rien ne garantit cet ordre - et un effet qui sort sur un jeton absent sans
  // jamais se rejouer laisse l'utilisateur sans notification pour la session.
  const [jeton, setJeton] = useState(() => getToken());
  useEffect(() => surChangementJeton(setJeton), []);

  useEffect(() => {
    if (!jeton) return undefined;
    stompClient.demarrer();
    demanderPermissionNotification();

    const surFocus = () => compteurDansTitre(0);
    window.addEventListener('focus', surFocus);

    const off = stompClient.souscrire('/user/queue/notifications', (ev) => {
      const p = ev.payload;
      // Un seul toast par message : MENTION et NOUVEAU_MESSAGE partagent l'identifiant,
      // et le serveur émet la mention en premier - c'est elle qui reste à l'écran.
      const toastId = `conv-${p?.conversationTrackingId}-${p?.sequence}`;

      if (ev.type === 'MENTION') {
        const ouverte = cheminRef.current === `/conversations/${p.conversationTrackingId}`;
        if (ouverte && document.hasFocus()) return;
        sonDiscret();
        toast.warning(i18n.t('commun:alertesConversations.mentionDans', { auteur: p.auteur, titre: p.titre, apercu: p.apercu }), {
          toastId,
          autoClose: 12000,
          onClick: () => navigate(`/conversations/${p.conversationTrackingId}`),
          style: { cursor: 'pointer' },
        });
        if (!document.hasFocus()) {
          notifierNavigateur(i18n.t('commun:alertesConversations.mention', { auteur: p.auteur }), `${p.titre} : ${p.apercu}`,
            () => navigate(`/conversations/${p.conversationTrackingId}`));
        }
        return;
      }

      if (ev.type !== 'NOUVEAU_MESSAGE') return;
      const total = p.totalNonLus ?? 0;
      window.dispatchEvent(new CustomEvent('cb:conversations-non-lus', { detail: total }));

      const ouverte = cheminRef.current === `/conversations/${p.conversationTrackingId}`;
      if (ouverte && document.hasFocus()) return;
      if (toast.isActive(toastId)) {
        // Déjà signalé par la mention : on ne rejoue ni le son ni le toast.
        if (!document.hasFocus()) compteurDansTitre(total);
        return;
      }

      const urgent = p.priorite === 'URGENT';
      sonDiscret();
      toast[urgent ? 'error' : 'info'](
        `${p.titre} - ${p.auteur} : ${p.apercu}`,
        {
          toastId,
          autoClose: urgent ? 12000 : 6000,
          onClick: () => navigate(`/conversations/${p.conversationTrackingId}`),
          style: { cursor: 'pointer' },
        },
      );
      if (!document.hasFocus()) {
        compteurDansTitre(total);
        notifierNavigateur(p.titre, `${p.auteur} : ${p.apercu}`,
          () => navigate(`/conversations/${p.conversationTrackingId}`));
      }
    });

    return () => { off(); window.removeEventListener('focus', surFocus); };
  }, [jeton, navigate]);

  return null;
}
